/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import Image from 'next/image'
import React from 'react'

import { Carousel } from '@/components/e-commerce/kits/kit-gallery-carousel/Carousel'
import { CarouselContent } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselContent'
import { CarouselItem } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselItem'
import { CarouselNext } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselNext'
import { CarouselPrevious } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselPrevious'
import { CarouselThumbnail } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselThumbnail'
import { CarouselThumbnails } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselThumbnails'
import { CarouselVimeoItem } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselVimeoItem'
import { CarouselVimeoThumbnail } from '@/components/e-commerce/kits/kit-gallery-carousel/CarouselVimeoThumbnail'

/** A description of available properties for this component. */
type KitGalleryCarouselProps = {
  /** The image URLs to render inside the gallery. */
  sources: Array<string>
}

/**
 * Mobile-friendly media gallery to display product media for an activity. On a mobile device, the main image of the
 * gallery sits at the top, with the thumbnails below. On larger devices, the thumbnails are on the left side of the
 * main image. Set to display three thumbnails currently.
 */
const KitGalleryCarousel: React.FC<KitGalleryCarouselProps> = ({ sources }) => {
  return (
    <Carousel className={'flex flex-col gap-6 bg-white shadow-2xl rounded-3xl p-4'} orientation={'horizontal'}>
      <div className={'relative basis-4/5 rounded-3xl overflow-hidden drop-shadow-product-gallery border border-black'}>
        <CarouselContent className={'h-[342px]'}>
          {sources.map((source, index) => {
            if (source.includes('vimeo.com')) {
              const videoId = source.split('/').pop()?.split('?')[0]
              return <CarouselVimeoItem key={index} videoId={videoId || ''} className={'h-full relative'} />
            }

            return (
              <CarouselItem key={index} className={'h-full relative'}>
                <Image src={source} alt={''} fill className={'object-cover'} />
                <div className={'bg-purple-dark h-full w-full absolute inset-0'} />
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <div className={'absolute inset-0 flex items-center justify-between pointer-events-none'}>
          <CarouselPrevious />
          <CarouselNext />
        </div>
      </div>

      <div className={'relative shrink-0 h-20'}>
        <CarouselThumbnails>
          {sources.map((source, index) => {
            if (source.includes('vimeo.com')) {
              const videoId = source.split('/').pop()?.split('?')[0]

              return (
                <CarouselVimeoThumbnail
                  key={index}
                  videoId={videoId || ''}
                  index={index}
                  className={'basis-1/5 grow-0 shrink-0 h-full'}
                />
              )
            }

            return (
              <CarouselThumbnail key={index} index={index} className={'basis-1/5 grow-0 shrink-0 h-full'}>
                <Image src={source} alt={''} fill className={'object-cover'} />
              </CarouselThumbnail>
            )
          })}
        </CarouselThumbnails>
      </div>
    </Carousel>
  )
}

export { KitGalleryCarousel }
